import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../header/header';
import './Licensing.css';
import { fetchUserBusinesses } from '../../redux/userBusinessesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { FiUsers, FiCheckCircle, FiAlertCircle, FiPlusCircle, FiRefreshCw, FiSearch, FiMonitor } from 'react-icons/fi';
import { FaKey, FaBriefcase, FaTabletAlt, FaMapMarkerAlt, FaInfoCircle, FaDesktop, FaCartPlus, FaCloud } from 'react-icons/fa';
import { api } from '../../api';
import InfoPanel from '../genericInfo/InfoPanel';
import Footer from '../footer/Footer';

const LicensingPage = () => {
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [licenseView, setLicenseView] = useState('combined'); // 'combined', 'standard', 'virtual'

  const userEmail = localStorage.getItem('user');

  const dispatch = useDispatch();

  const { items: userBusinesses } = useSelector((state) => state.userBusinesses);

  // sort user businesses by name
  const sortedBusinesses = [...userBusinesses].sort((a, b) =>
    a.business_name.localeCompare(b.business_name)
  );

  // check if small screen
  const isSmallScreen = window.innerWidth < 768;

  //set selected business to first business if none is selected
  useEffect(() => {
    if (userBusinesses.length > 0 && !selectedBusiness) {
      setSelectedBusiness(userBusinesses[0].business_id);
    }
  }, [userBusinesses, selectedBusiness]);

  useEffect(() => {
    if (userEmail) {
      console.log(`Fetching user businesses for email in licensing page ${userEmail}`);
      dispatch(fetchUserBusinesses(userEmail));
    }
  }, [dispatch, userEmail]);

  const fetchData = useCallback(async () => {
    if (selectedBusiness) {
      setLoading(true);
      try {
        const [licenseResponse, devicesResponse] = await Promise.all([
          api.get(`/licensing/${selectedBusiness}/license`),
          api.get(`/licensing/${selectedBusiness}/devices`)
        ]);
        setLicenseInfo(licenseResponse.data);
        setDevices(devicesResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBusinessChange = (e) => {
    setSelectedBusiness(e.target.value);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleSuspend = (deviceId) => {
    if (window.confirm('Are you sure you want to suspend this device?')) {
      setDevices(devices.map(device => 
        device.device_id === deviceId ? { ...device, active: 0 } : device
      ));
    }
  };

  const filteredDevices = useMemo(() => {
    return devices
      .filter(device => {
        if (statusFilter === 'all') return true;
        return statusFilter === 'active' ? device.active : !device.active;
      })
      .filter(device => 
        device.device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.store_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [devices, searchTerm, statusFilter]);

  const groupedDevices = useMemo(() => {
    const sortedStores = [...new Set(filteredDevices.map(d => d.store_name))].sort();
    return sortedStores.reduce((acc, store_name) => {
      acc[store_name] = filteredDevices
        .filter(d => d.store_name === store_name)
        .sort((a, b) => {
          if (a.active === b.active) {
            return (a.device_name || '').localeCompare(b.device_name || '');
          }
          return a.active ? -1 : 1; // Active devices first
        });
      return acc;
    }, {});
  }, [filteredDevices]);

  // Calculate over limits for both license types
  const standardOverLimit = licenseInfo ? 
    Math.max(0, licenseInfo.standard_seats_used - licenseInfo.standard_seats_total) : 0;
  const virtualOverLimit = licenseInfo ? 
    Math.max(0, licenseInfo.virtual_seats_used - licenseInfo.virtual_seats_total) : 0;
  const totalOverLimit = standardOverLimit + virtualOverLimit;

  // Render license summary cards based on current view
  const renderLicenseSummary = () => {
    if (!licenseInfo) return null;

    switch (licenseView) {
      case 'standard':
        return (
          <div className="licensing-summary">
            <div className="summary-card standard-license">
              <FiMonitor size={20} color="#667eea" />
              <h2>Standard Total</h2>
              <p>{licenseInfo.standard_seats_total}</p>
            </div>
            <div className="summary-card standard-license">
              <FiCheckCircle size={20} color="#22c55e" />
              <h2>Standard Used</h2>
              <p>{licenseInfo.standard_seats_used}</p>
            </div>
            <div className="summary-card standard-license">
              <FiPlusCircle size={20} color="#f59e0b" />
              <h2>Standard Available</h2>
              <p>{licenseInfo.standard_seats_available}</p>
            </div>
            <div className={`summary-card standard-license ${standardOverLimit > 0 ? 'over-limit' : ''}`}>
              <FiAlertCircle size={20} color="#ef4444" />
              <h2>Standard Over Limit</h2>
              <p>{standardOverLimit}</p>
            </div>
          </div>
        );

      case 'virtual':
        return (
          <div className="licensing-summary">
            <div className="summary-card virtual-license">
              <FaCloud size={20} color="#8b5cf6" />
              <h2>Virtual Total</h2>
              <p>{licenseInfo.virtual_seats_total}</p>
            </div>
            <div className="summary-card virtual-license">
              <FiCheckCircle size={20} color="#22c55e" />
              <h2>Virtual Used</h2>
              <p>{licenseInfo.virtual_seats_used}</p>
            </div>
            <div className="summary-card virtual-license">
              <FiPlusCircle size={20} color="#f59e0b" />
              <h2>Virtual Available</h2>
              <p>{licenseInfo.virtual_seats_available}</p>
            </div>
            <div className={`summary-card virtual-license ${virtualOverLimit > 0 ? 'over-limit' : ''}`}>
              <FiAlertCircle size={20} color="#ef4444" />
              <h2>Virtual Over Limit</h2>
              <p>{virtualOverLimit}</p>
            </div>
          </div>
        );

      default: // 'combined'
        return (
          <>
            {/* Combined Overview */}
            <div className="licensing-summary">
              <div className="summary-card">
                <FiUsers size={20} color="#667eea" />
                <h2>Total Seats</h2>
                <p>{licenseInfo.total_seats_total}</p>
              </div>
              <div className="summary-card">
                <FiCheckCircle size={20} color="#22c55e" />
                <h2>Total Used</h2>
                <p>{licenseInfo.total_seats_used}</p>
              </div>
              <div className="summary-card">
                <FiPlusCircle size={20} color="#f59e0b" />
                <h2>Total Available</h2>
                <p>{licenseInfo.total_seats_total - licenseInfo.total_seats_used}</p>
              </div>
              <div className={`summary-card ${totalOverLimit > 0 ? 'over-limit' : ''}`}>
                <FiAlertCircle size={20} color="#ef4444" />
                <h2>Over Limit</h2>
                <p>{totalOverLimit}</p>
              </div>
            </div>

            {/* Detailed Breakdown */}
            <div className="license-breakdown">
              <div className="breakdown-section">
                <div className="breakdown-header">
                  <FiMonitor size={18} color="#667eea" />
                  <h3>Standard Licenses</h3>
                </div>
                <div className="breakdown-stats">
                  <span className="stat">
                    <span className="stat-label">Used:</span>
                    <span className="stat-value">{licenseInfo.standard_seats_used}</span>
                  </span>
                  <span className="stat">
                    <span className="stat-label">Total:</span>
                    <span className="stat-value">{licenseInfo.standard_seats_total}</span>
                  </span>
                  <span className="stat">
                    <span className="stat-label">Available:</span>
                    <span className="stat-value available">{licenseInfo.standard_seats_available}</span>
                  </span>
                  {standardOverLimit > 0 && (
                    <span className="stat over-limit">
                      <span className="stat-label">Over Limit:</span>
                      <span className="stat-value">{standardOverLimit}</span>
                    </span>
                  )}
                </div>
              </div>

              <div className="breakdown-section">
                <div className="breakdown-header">
                  <FaCloud size={18} color="#8b5cf6" />
                  <h3>Virtual Licenses</h3>
                </div>
                <div className="breakdown-stats">
                  <span className="stat">
                    <span className="stat-label">Used:</span>
                    <span className="stat-value">{licenseInfo.virtual_seats_used}</span>
                  </span>
                  <span className="stat">
                    <span className="stat-label">Total:</span>
                    <span className="stat-value">{licenseInfo.virtual_seats_total}</span>
                  </span>
                  <span className="stat">
                    <span className="stat-label">Available:</span>
                    <span className="stat-value available">{licenseInfo.virtual_seats_available}</span>
                  </span>
                  {virtualOverLimit > 0 && (
                    <span className="stat over-limit">
                      <span className="stat-label">Over Limit:</span>
                      <span className="stat-value">{virtualOverLimit}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="licensing-container">
      <Header />
      
      {/* Header Section - Compact */}
      <div className="licensing-header">
        <div>
          <FaKey className='licensing-header-text' color="#667eea" style={{ marginRight: '6px' }} />
          <span className='licensing-header-text'>FDBK Licensing</span>
          {/* Info Section - Compact */}
          <InfoPanel
            title="Licensing Info"
            direction={isSmallScreen ? 'bottom' : 'right'}
            items={[
              "Standard licenses: Physical device seats",
              "Virtual licenses: Cloud/software-based seats",
              "Each active device consumes one license seat",
              "Devices over limit cannot send feedback data",
              "Suspend devices to free up seats",
              "Contact support to upgrade licenses"
            ]}
          />
        </div>
        <button onClick={handleRefresh} className="refresh-button" disabled={loading}>
          <FiRefreshCw />
          Refresh Data
        </button>
      </div>

      {/* Controls Section - Compact */}
      <div className="controls-container">
        <div className="business-selector">
          <label htmlFor="business-select" className='select-business-label'>
            <FaBriefcase color="#667eea" style={{ marginRight: '6px' }} /> Select Business
          </label>
          <select id="business-select" onChange={handleBusinessChange} value={selectedBusiness} disabled={loading}>
            {sortedBusinesses.map((business) => (
              <option key={business.business_id} value={business.business_id}>
                {business.business_name}
              </option>
            ))}
          </select>
        </div>
        
        {/* License View Toggle */}
        {licenseInfo && (
          <div className="license-view-toggle">
            <label>License View:</label>
            <select value={licenseView} onChange={e => setLicenseView(e.target.value)}>
              <option value="combined">Combined View</option>
              <option value="standard">Standard Only</option>
              <option value="virtual">Virtual Only</option>
            </select>
          </div>
        )}

        <div className="filters-container">
          <div className="search-bar">
            <FiSearch />
            <input 
              type="text" 
              placeholder="Search devices..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select onChange={e => setStatusFilter(e.target.value)} value={statusFilter}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {loading && <p style={{textAlign: 'center', margin: '20px 0'}}>Loading...</p>}

      {!selectedBusiness && !loading && <p style={{textAlign: 'center', margin: '20px 0'}}>Please select a business to view licensing information.</p>}

      {selectedBusiness && !loading && licenseInfo && (
        <>
          {/* License Summary */}
          {renderLicenseSummary()}

          {/* Device List - Takes Most Space */}
          <div className="device-list">
            <h2>
              <FaTabletAlt size={20} color="#667eea" style={{ marginRight: '8px' }} />
              Registered Devices
              <span className="device-count">({licenseInfo.active_devices_count} active)</span>
            </h2>
            
            {/* IMPORTANT: This wrapper makes the table scrollable */}
            <div className="device-list-content">
              {Object.entries(groupedDevices).map(([storeName, storeDevices]) => (
                <div key={storeName} className="store-group">
                  <h3>
                    <FaMapMarkerAlt size={16} color="#667eea" style={{ marginRight: '6px' }} />
                    {storeName} ({storeDevices.length} Devices)
                  </h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Device ID</th>
                        <th>Device Name</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Last Sync</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storeDevices.map((device) => (
                        <tr key={device.device_id}>
                          <td>
                            <FaDesktop size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            {device.device_id}
                          </td>
                          <td>{device.device_name || 'N/A'}</td>
                          <td>
                            <span className="device-type-badge standard">
                              <FiMonitor size={12} />
                              Standard
                            </span>
                            {/* TODO: Add logic to determine device type based on your business rules */}
                          </td>
                          <td className={device.active ? 'text-green-600' : 'text-red-600'}>
                            {device.active ? 'Active' : 'Inactive'}
                          </td>
                          <td>2025-07-31 12:34:56</td>
                          <td>
                            <button 
                              onClick={() => handleSuspend(device.device_id)} 
                              className="detach-button"
                              disabled={!device.active}
                            >
                              {device.active ? 'Suspend' : 'Suspended'}
                            </button> 
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {Object.keys(groupedDevices).length === 0 && (
                <div className="no-devices">
                  <FaInfoCircle size={24} color="#6b7280" />
                  <p>No devices found matching current filters.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  );
};

export default LicensingPage;